/**
 * The round, replayed.
 *
 * A player watches their own cast the instant the server answers, and then sees
 * everyone else's when the cycle turns over — because that is when the server tells
 * anyone what happened. There is no per-action event for a player: `action_accepted`
 * and `action_performed` are published to the master channel on purpose, and the
 * board's own `world::new_cycle` refresh of the action log is the only moment a
 * player learns that an enemy did something. So the reel is a DIFF of that log
 * against what has already been drawn, not a stream.
 *
 * Two rules hold the whole thing together:
 *
 *  - THE CELLS ARE THE SERVER'S. `TacticalTargetResolver` stashes the cells it
 *    resolved back onto `data.tactical_aims[i].affected_cells`, and this module reads
 *    exactly those. Recomputing the pattern here would draw the blast the client
 *    THINKS happened, which is the one thing a replay must never do — the server
 *    clips for cover, and the point of the reel is to show what actually landed.
 *
 *  - AN ACTION IS PLAYED ONCE. The log is a rolling window over the last few cycles,
 *    so every refresh re-delivers actions already on screen. Identity is the action
 *    id; a set of ids is all the memory needed.
 *
 * Everything here is pure. The board owns the ids it has played, the fog, and the
 * positions; this module owns the ordering and the shape of each cast.
 */

import { castPresentation, BATTLE_RENDER_DEFAULTS } from './presentation';

/** Actions whose whole story is the token moving — the board animates those itself. */
const NOT_AN_IMPACT = new Set(['MOVE', 'TACTICAL_MOVE', 'INSPECT', 'DICE_ROLL']);

/**
 * The cells each charge covered, in {col,row}.
 *
 * A charge that resolved before the server started recording cells falls back to its
 * own aim point: one cell is a poorer picture than the real footprint, but it is
 * still the truth about where the action was aimed, and it keeps older log entries
 * from vanishing out of the reel entirely.
 */
export function chargeCellsOf(action) {
  const aims = action?.data?.tactical_aims;
  if (!Array.isArray(aims)) return [];
  return aims.map(aim => {
    const cells = Array.isArray(aim?.affected_cells) ? aim.affected_cells : null;
    if (cells && cells.length) {
      return cells.map(cell => ({ col: cell.x, row: cell.y }));
    }
    if (Number.isFinite(aim?.x) && Number.isFinite(aim?.y)) {
      return [{ col: aim.x, row: aim.y }];
    }
    return [];
  });
}

/** The cell a charge was aimed at, which is where its trace lands. */
function aimPointOf(action, index, cells) {
  const aim = action?.data?.tactical_aims?.[index];
  if (Number.isFinite(aim?.x) && Number.isFinite(aim?.y)) return { col: aim.x, row: aim.y };
  if (!cells.length) return null;
  const col = Math.round(cells.reduce((sum, cell) => sum + cell.col, 0) / cells.length);
  const row = Math.round(cells.reduce((sum, cell) => sum + cell.row, 0) / cells.length);
  return { col, row };
}

export function cycleNumberOf(action) {
  const cycle = action?.cycle;
  if (typeof cycle === 'number') return cycle;
  return Number(cycle?.number ?? 0);
}

/** Whether this action is one the board draws at all, rather than one the tokens tell. */
export function drawsAnImpact(action) {
  return !!action && !NOT_AN_IMPACT.has(action.action_type);
}

/**
 * What is new in this log, in the order it happened.
 *
 * Only performed actions: an accepted-but-not-yet-performed action has no impacts and
 * has not touched anyone, and replaying it would show the blast before the round
 * resolved it.
 */
export function pendingReelActions(log, played = new Set()) {
  return (Array.isArray(log) ? log : [])
    .filter(action => action?.id && action.performed && !played.has(action.id))
    .filter(action => !NOT_AN_IMPACT.has(action.action_type))
    .slice()
    .sort((a, b) => (cycleNumberOf(a) - cycleNumberOf(b)) || ((a.order ?? 0) - (b.order ?? 0)));
}

/**
 * The impact this charge produced.
 *
 * `ActionImpact.charge_index` says which charge a resolved impact came from, so a
 * volley whose first bolt froze someone and whose second burned them draws two
 * different materials. The field is recent: entries written before it exists carry
 * none, and for those the first resolved impact still describes every charge —
 * material and intent are properties of the skill far more often than of the
 * individual hit. A charge that resolved nothing (a miss, an empty cell) in an
 * action that DOES carry the field falls through to the skill's declared impact
 * rather than borrowing another charge's.
 */
function impactForCharge(impacts, index) {
  if (!impacts.length) return null;
  const indexed = impacts.some(impact => Number.isFinite(impact?.charge_index));
  if (!indexed) return impacts[0];
  return impacts.find(impact => impact?.charge_index === index) || null;
}

/**
 * One log entry as the casts it should draw — one per charge.
 *
 * `positionOf` is the board's, because only the board knows where a token stands right
 * now. The caster matters for more than bookkeeping: a `shot` or a `melee` is traced
 * FROM the caster's cell, so a missing caster turns a sword swing into something that
 * materialises out of the air, while `bless`/`hex` descend and do not care.
 */
export function castsForAction(action, { positionOf = () => null, defaults = BATTLE_RENDER_DEFAULTS, ability: castAbility = null } = {}) {
  const charges = chargeCellsOf(action);
  const impacts = Array.isArray(action?.impacts) ? action.impacts : [];

  /*
   * `CharacterActionLogSerializer` nests the whole skill; the submit response
   * (`CharacterActionSerializer`) sends a bare id and no impacts at all. A bare id
   * carries no type, no violation and no icon, so it is read as NO ability rather
   * than as an ability with nothing in it — the caller that has the real object in
   * hand (the board that just fired it) passes it in instead.
   */
  const ability = castAbility || (action?.skill && typeof action.skill === 'object' ? action.skill : null);

  const fromCells = charges
    .map((cells, index) => ({ cells, index }))
    .filter(charge => charge.cells.length);

  if (!fromCells.length) {
    // No tactical aim at all — an action targeted by character rather than by ground.
    const targets = Array.isArray(action?.targets) ? action.targets : [];
    const cells = targets.map(id => positionOf(id)).filter(Boolean);
    if (!cells.length || !ability) return [];
    fromCells.push({ cells, index: 0 });
  }

  const caster = positionOf(action.initiator);

  return fromCells.map(({ cells, index }) => {
    const target = aimPointOf(action, index, cells) || cells[0];
    return castPresentation(ability, { caster, target, cells }, {
      id: `${action.id}:${index}`,
      impact: impactForCharge(impacts, index),
      seed: index + 1,
      defaults,
    });
  });
}

/**
 * The whole reel: every action the log gained, as casts, in play order.
 *
 * The ids are returned alongside so the caller can mark them played BEFORE awaiting
 * the animations — a second refresh landing mid-reel must not queue the same round
 * twice.
 */
export function buildReel(log, played = new Set(), options = {}) {
  const actions = pendingReelActions(log, played);
  const casts = [];
  actions.forEach(action => {
    castsForAction(action, options).forEach(cast => casts.push(cast));
  });
  return { casts, actionIds: actions.map(action => action.id) };
}

/**
 * Fields the GM feed nests that the reel reads as plain ids.
 *
 * `GameMasterCharacterActionLogSerializer` runs at `depth=1`, so every FK it carries
 * arrives as an object; the player's own log sends ids. THIS FUNCTION IS THE ONLY
 * PLACE THE TWO SHAPES ARE RECONCILED — nothing downstream of it re-checks, so a
 * field added to what the reel reads must be added to one of these lists at the same
 * time or it reaches `castsForAction` as an object where an id is expected.
 *
 * `skill` is deliberately absent: the reel wants the whole nested skill object, and
 * `depth=1` is exactly what supplies it.
 */
const GM_ID_FIELDS = ['initiator'];
const GM_ID_LIST_FIELDS = ['targets'];

/** One GM feed entry in the shape `pendingReelActions`/`castsForAction` speak. */
export function normalizeGmAction(action) {
  if (!action) return action;
  const normalized = { ...action };
  GM_ID_FIELDS.forEach(field => {
    if (field in normalized) normalized[field] = normalized[field]?.id ?? normalized[field];
  });
  GM_ID_LIST_FIELDS.forEach(field => {
    if (field in normalized) {
      normalized[field] = (normalized[field] || []).map(entry => entry?.id ?? entry);
    }
  });
  return normalized;
}

export default buildReel;
